/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { IconDownload, IconSearch, IconX, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ListView from "../../../components/common/ListView";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Tooltip from "../../../components/common/Tooltip";
import useDebounce from "../../../utilities/useDebounce";
import { useFetchProductItemInventoryData } from "../../../hooks/inventoryHooks";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../../utilities/helper";
import * as XLSX from "xlsx";

interface ItemInventoryProps {
  headerSubTabs?: React.ReactNode;
}

export default function ItemInventory({ headerSubTabs }: ItemInventoryProps = {}) {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const debouncedSearchFor = useDebounce(searchFor, 550);

  const profile = useSelector((state: any) => state.auth.profile);
  const warehouseId = profile?.warehouse;

  const { data, isLoading, isFetching, error, refetch } =
    useFetchProductItemInventoryData(pageNo, limit, debouncedSearchFor, undefined, warehouseId);

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor, warehouseId]);

  const totalPages = data?.totalPages ?? 0;

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setPageNo(page);
  };

  const renderPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pageNo > 3) pages.push("...");
      for (let i = Math.max(2, pageNo - 1); i <= Math.min(totalPages - 1, pageNo + 1); i++) {
        pages.push(i);
      }
      if (pageNo < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const handleDownloadXLSX = () => {
    if (!data?.inventory || data.inventory.length === 0) {
      return;
    }
    const visibleColumns = columnsData.filter((col) => col.isShow);
    const exportData = data.inventory.map((item: any, index: number) => {
      const row: any = {};
      visibleColumns.forEach((col) => {
        if (col.value === "sn") {
          row[col.text] = (pageNo - 1) * limit + (index + 1);
        } else if (col.value === "itemType") {
          row["Item Type"] = item.storage !== "BIN" ? "B2C" : "B2B";
        } else if (col.value === "shelveID") {
          row["Location"] = item.locationName || "";
        } else if (col.value === "itemName") {
          row["Item Name"] = item.itemName || "";
        } else if (col.value === "sku") {
          row["SKU"] = item.sku || "";
        } else if (col.value === "storageType") {
          row["Storage Type"] = item.locationType || "";
        } else if (col.value === "capacity") {
          row["Capacity"] = item.capacity || "";
        } else if (col.value === "qty") {
          row["Avl. Qty"] = item.quantity ?? 0;
        } else if (col.value === "inventoryType") {
          row["Inventory Type"] = item.isReserved ? "Reserved" : "Received";
        } else if (col.value === "expiryDate") {
          row["Expiry Date"] = item.expiryDate || "";
        } else if (col.value === "createdAt") {
          row["Created At"] = item.createdAt || "";
        } else if (col.value === "status") {
          row["Expiry Status"] = item.expiryDate ? "Has Expiry" : "No Expiry";
        } else if (col.value === "updatedAt") {
          row["Last Updated"] = item.updatedAt || "";
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Item_Inventory");
    XLSX.writeFile(wb, "item_inventory.xlsx");
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "Item Type", value: "itemType", isShow: true },
    { text: "Bin Location", value: "shelveID", isShow: true },
    { text: "Item", value: "itemName", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    { text: "Storage Type", value: "storageType", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "Avl. Qty.", value: "qty", isShow: true },
    { text: "Inventory Type", value: "inventoryType", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Expiry Date", value: "expiryDate", isShow: true },
    { text: "Created At", value: "createdAt", isShow: true },
    { text: "Last Updated", value: "updatedAt", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "itemType":
          return {
            label: "Item Type",
            render: (c: any, index: any) => (
              <div className="flex">
                <span
                  key={index}
                  title={c.itemName}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-slate-100 text-slate-800 border-slate-200"
                >
                  {c?.storage !== "BIN" ? "B2C" : "B2B"}
                </span>
              </div>
            ),
          };
        case "shelveID":
          return {
            label: "Location",
            render: (c: any) => {
              const name = c.locationName || "";
              if (!name) return <div className="min-w-[140px] max-w-[160px] h-12 mx-auto"></div>;

              return (
                <div className="min-w-[140px] max-w-[160px] flex flex-col items-center justify-center space-y-1 text-center mx-auto">
                  <div className="h-9 w-full overflow-hidden flex justify-center items-center">
                    <img
                      alt=""
                      src={
                        c?.binLocationBarcode
                          ? c.binLocationBarcode
                          : "/img/bin-location-placeholder.png"
                      }
                      className="object-cover object-top w-full h-11"
                    />
                  </div>
                  <span
                    title={name}
                    className="text-[11px] font-semibold text-[#131327]/70 text-center w-full block truncate"
                  >
                    {name}
                  </span>
                </div>
              );
            },
          };
        case "itemName":
          return {
            label: "Items",
            render: (c: any) => (
              <div className="flex items-center space-x-3 truncate max-w-xs">
                <img
                  alt={c.itemName}
                  src={c.image || "/img/product-placeholder.png"}
                  className="size-12 rounded-md object-cover object-center "
                />
                <span title={c.itemName} className="truncate w-20">
                  {c.itemName}
                </span>
              </div>
            ),
          };
        case "sku":
          return {
            label: "SKU",
            render: (c: any) => {
              const skuStr = c?.sku || "";
              return (
                <span
                  title={skuStr}
                  className="block truncate max-w-[130px] mx-auto text-xs font-semibold text-[#131327]/70"
                >
                  {skuStr || "---"}
                </span>
              );
            },
          };
        case "storageType":
          return {
            label: "Storage Type",
            render: (c: any) => c.locationType || "BIN",
          };
        case "capacity":
          return {
            label: "Capacity",
            render: (c: any) => c.capacity || "---",
          };
        case "qty":
          return {
            label: "Avl. Qty",
            render: (c: any) => c.quantity ?? "-",
          };
        case "inventoryType":
          return {
            label: "Inventory Type",
            render: (c: any) => {
              const type = c.isReserved ? "Reserved" : "Received";
              return (
                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-slate-100 text-slate-800 border-slate-200">
                  {type}
                </span>
              );
            },
          };
        case "expiryDate":
          return {
            label: "Expiry Date",
            render: (c: any) => {
              if (!c?.expiryDate) return <div></div>;
              return <div>{c?.expiryDate}</div>;
            },
          };
        case "status":
          return {
            label: "Expiry Status",
            render: (c: any) => {
              if (!c?.expiryDate)
                return <div style={{ color: "gray" }}>No Expiry</div>;

              const now = new Date();
              const expiry = new Date(c.expiryDate);
              // Normalize to end of day so it doesn't expire at 00:00 UTC
              expiry.setHours(23, 59, 59, 999);

              const diffMs = expiry.getTime() - now.getTime();

              // 1. Actually Expired (Past the end of the day)
              if (diffMs <= 0) {
                return (
                  <div style={{ color: "red", fontWeight: "bold" }}>
                    Expired
                  </div>
                );
              }

              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

              // 2. Expiring Today
              if (diffHours < 24) {
                return (
                  <div style={{ color: "red", fontWeight: "bold" }}>
                    Expires in {diffHours} hours
                  </div>
                );
              }

              // 3. Expiring Soon
              if (diffDays <= 7) {
                return (
                  <div style={{ color: "orange", fontWeight: "bold" }}>
                    Expires in {diffDays} days
                  </div>
                );
              }

              // 4. Healthy
              return (
                <div style={{ color: "green" }}>Active ({diffDays} days)</div>
              );
            },
          };
        case "createdAt":
          return {
            label: "Created At",
            render: (c: any) => formatDateTime(c.createdAt),
          };
        case "updatedAt":
          return {
            label: "Last Updated",
            render: (c: any) => formatDateTime(c.updatedAt),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  return (
    <>
      {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
      <div className="relative grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-r-3xl">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="col-span-12 z-10">
          <div className="rounded-3xl overflow-visible" style={{ minHeight: "150px" }}>
            <div className="px-6 py-1.5">
          {/* Header Row (Filters + Actions Unified) */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left side: Badge */}
            <div className="flex flex-wrap items-center gap-3">
              {headerSubTabs}
            </div>

            {/* Right side: Action controls (Search, Export, Entries, Columns) */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Search */}
              <div className="relative flex items-center group">
                <div
                  onClick={() => !showSearch && setShowSearch(true)}
                  className={`flex items-center transition-all duration-300 ease-in-out border border-[#E4E5E7] rounded-lg bg-white shadow-sm h-8 ${showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150"}`}
                >
                  <button onClick={(e) => { e.stopPropagation(); setShowSearch(!showSearch); if (showSearch) { setSearchFor(""); setPageNo(1); } }} className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer">
                    <IconSearch size={16} />
                  </button>
                  {showSearch && (
                    <>
                      <input type="text" value={searchFor} onChange={(e) => { setSearchFor(e.target.value); setPageNo(1); }}
                        placeholder="Search..." autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                      />
                      <button onClick={(e) => { e.stopPropagation(); setSearchFor(""); setShowSearch(false); }}
                        className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                      >
                        <IconX size={14} stroke={2.5} />
                      </button>
                    </>
                  )}
                </div>
                {!showSearch && (
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                    Search
                  </div>
                )}
              </div>

              {/* Export/Download */}
              <div className="relative group">
                <button
                  onClick={handleDownloadXLSX}
                  className="h-8 w-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                >
                  <IconDownload size={18} stroke={2.5} />
                </button>
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                  Export to Excel
                </div>
              </div>

              {/* Entries Limit Selector */}
              {data && data.totalPages * limit > 10 && (
                <div className="relative group">
                  <EntriesSelector
                    value={limit}
                    options={[
                      10,
                      ...(data.totalPages * limit > 10 ? [20] : []),
                      ...(data.totalPages * limit > 20 ? [30] : []),
                      ...(data.totalPages * limit > 50 ? [50] : []),
                      ...(data.totalPages * limit > 100 ? [100] : []),
                    ]}
                    onChange={(val) => {
                      setLimit(val);
                      setPageNo(1);
                    }}
                  />
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                    Entries Limit
                  </div>
                </div>
              )}

              {/* Columns Hide/Show Filter */}
              <div className="relative group">
                <ColumnsFilter
                  triggerClassName="border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                  columnsData={columnsData}
                  setHeadings={setHeadings}
                />
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50 animate-in fade-in slide-in-from-bottom-1 duration-100">
                  Columns View
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Container (Joined) */}
        <div className="border-t border-slate-100">
          {isLoading || isFetching ? (
            <TableSkeleton
              columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
              rows={6}
            />
          ) : data?.inventory?.length ? (
            <ListView
              data={data.inventory}
              columns={columns}
              trClassName="transition-colors duration-150"
              tdClassName="!px-2 !py-2.5"
              thClassName="!px-2 !py-2.5"
            />
          ) : (
            <NoDataFound />
          )}
        </div>

        {/* Styled custom pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center gap-4 py-4 px-6 select-none border-t border-slate-100 animate-fadeIn">
            <div className="flex items-center gap-1">
              <Tooltip text="Previous Page">
                <button
                  onClick={() => goToPage(pageNo - 1)}
                  disabled={pageNo === 1}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                >
                  <IconChevronLeft size={14} stroke={2.5} />
                </button>
              </Tooltip>
              {renderPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                  >
                    …
                  </span>
                ) : (
                  <Tooltip key={p} text={`Page ${p}`}>
                    <button
                      onClick={() => goToPage(p as number)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm ${
                        pageNo === p
                          ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {p}
                    </button>
                  </Tooltip>
                )
              )}
              <Tooltip text="Next Page">
                <button
                  onClick={() => goToPage(pageNo + 1)}
                  disabled={pageNo === totalPages}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
                >
                  <IconChevronRight size={14} stroke={2.5} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </>
  );
}
